// The action ladder on a Needs you row: the specific fix as the primary, then ⋯ with Check again, Open PO in Syncore,
// "Upload a corrected invoice…" and "Reject invoice…". The slide-out and the full review lay the same actions out by
// the problem instead (problem.tsx), under the same names. Buttons go away while work runs.
import type { Invoice, InvoiceDetail } from "../api/types";
import { checkForCode, plainSummary } from "./checks";
import { Hint, Spinner, mono } from "./components";
import { Button, Icon, IconButton } from "../ui";
import type { InvoiceActions } from "../screens/ProblemDetail";

export type FixKind = "asinvoiced" | "approve" | "setpo" | "supplier" | "billto" | "api" | "signin" | "duplicate" | "none";

/** Outcome codes that mean a Syncore setting is missing or rejected rather than anything about the invoice. */
export const API_SETUP_CODES = ["MISSING_CREDENTIALS", "SYNCORE_API_401", "SYNCORE_API_403"];

/** Which setting a stopped invoice is waiting on, if any. */
export function setupGapOf(invoice: Pick<Invoice, "code" | "summary" | "status">): "api" | "signin" | null {
  if (invoice.status !== "Failed") return null;
  if (invoice.code === "SYNCORE_LOGIN_FAILED") return "signin";
  if (invoice.code === "MISSING_CREDENTIALS") return /attach|email|sign-in|password|totp/i.test(invoice.summary ?? "") ? "signin" : "api";
  return invoice.code === "SYNCORE_API_401" || invoice.code === "SYNCORE_API_403" ? "api" : null;
}

/** The fix the ladder leads with. `asInvoiced` comes from the API: freight or lines differ, and the PO can be made to match (not in preview). */
export function fixKindOf(invoice: Pick<Invoice, "code" | "summary" | "status" | "asInvoiced">): FixKind {
  const gap = setupGapOf(invoice);
  if (gap) return gap;
  if (invoice.code === "OVER_LIMIT" && invoice.status === "NeedsReview") return "approve";
  switch (invoice.code) {
    case "NO_PO_REFERENCE": case "PO_REFERENCE_FORMAT": case "PO_NOT_FOUND": case "MULTIPLE_PO_REFERENCES": return "setpo";
    case "SUPPLIER_MISMATCH": case "SUPPLIER_UNKNOWN": return "supplier";
    case "BILL_TO_MISMATCH": return "billto";
    default:
      // Another invoice with this number was already approved: the answer is nearly always to reject this one as a duplicate.
      if (checkForCode(invoice.code) === "Not a duplicate") return "duplicate";
      return invoice.asInvoiced ? "asinvoiced" : "none";
  }
}

/** True while AutoVouch is reading or checking the invoice: the buttons are away. */
export function isWorking(invoice: Pick<Invoice, "status">): boolean {
  return invoice.status === "Queued" || invoice.status === "Processing";
}

export const PRIMARY: Record<Exclude<FixKind, "none">, [string, string]> = {
  asinvoiced: ["Approve as invoiced", "check"],
  approve: ["Approve now", "check"],
  setpo: ["Set PO number…", "hash"],
  supplier: ["This supplier is OK", "badge-check"],
  billto: ["Add this name to Billed to", "building-2"],
  api: ["Open Syncore settings", "settings"],
  signin: ["Open Syncore settings", "settings"],
  duplicate: ["Reject as duplicate", "x"],
};

export interface LadderAction {
  label: string;
  icon: string;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
  tip?: string;
  link?: boolean;
}

export interface Ladder {
  kind: FixKind;
  primary: LadderAction | null;
  rest: LadderAction[];
  /** In ⋯ only, after a divider: a corrected invoice replaces this one. */
  replace: LadderAction;
  /** Always last, in ⋯ only: the record stays as Not approved with a reason. */
  close: LadderAction;
}

/** The ordered actions for an invoice. `detail` sharpens the fix (the PO's figures); without it the row's data is enough. */
export function ladderFor(invoice: Invoice, actions: InvoiceActions, opts: { detail?: InvoiceDetail | null; billTo?: string | null; navigate: (to: string) => void }): Ladder {
  const kind = fixKindOf(invoice);
  const again: LadderAction = {
    label: "Check again", icon: "refresh-cw", onClick: () => actions.checkAgain([invoice.id]),
    disabled: kind === "api", tip: kind === "api" ? "Checking again fails until the Syncore API key is set up." : "Reads the PO from Syncore again and re-runs all 13 checks",
  };
  let primary: LadderAction | null = null;
  switch (kind) {
    case "approve": primary = { label: PRIMARY.approve[0], icon: PRIMARY.approve[1], primary: true, tip: "Every check passed; approves the PO in Syncore now, over your limit", onClick: () => actions.approveOverLimit(invoice) }; break;
    case "asinvoiced": primary = { label: PRIMARY.asinvoiced[0], icon: PRIMARY.asinvoiced[1], primary: true, tip: "Sets the PO's freight and lines to the invoice's in Syncore, checks again and approves", onClick: () => actions.approveAsInvoiced(invoice) }; break;
    case "setpo": primary = { label: PRIMARY.setpo[0], icon: PRIMARY.setpo[1], primary: true, onClick: () => actions.setPo(invoice) }; break;
    case "supplier": primary = { label: PRIMARY.supplier[0], icon: PRIMARY.supplier[1], primary: true, onClick: () => actions.supplierOk(invoice.id) }; break;
    case "billto": primary = { label: PRIMARY.billto[0], icon: PRIMARY.billto[1], primary: true, onClick: () => opts.navigate(`/settings/matching?billTo=${encodeURIComponent(opts.billTo ?? billToOf(invoice) ?? "")}`) }; break;
    case "api": case "signin": primary = { label: PRIMARY[kind][0], icon: PRIMARY[kind][1], primary: true, onClick: () => opts.navigate("/settings/syncore") }; break;
    // No dialog: the reason "Duplicate" is recorded and the row leaves at once. A different reason is still a click away under ⋯.
    case "duplicate": primary = { label: PRIMARY.duplicate[0], icon: PRIMARY.duplicate[1], primary: true, tip: "Marks it Not approved with the reason Duplicate; nothing is sent to Syncore", onClick: () => actions.rejectNow(invoice, "Duplicate") }; break;
    default: break;
  }
  const rest: LadderAction[] = [];
  if (primary) rest.push(again); else primary = { ...again, primary: true };
  const url = opts.detail?.po?.syncoreUrl;
  if (url) rest.push({ label: "Open PO in Syncore", icon: "external-link", link: true, onClick: () => window.open(url, "_blank", "noopener") });
  return {
    kind, primary, rest,
    replace: { label: "Upload a corrected invoice…", icon: "upload", onClick: () => actions.replace(invoice) },
    close: { label: "Reject invoice…", icon: "x", onClick: () => actions.resolve(invoice) },
  };
}

/** "Invoice is billed to Walker-Clay, Inc, not LogoMedia" → "Walker-Clay, Inc". */
export function billToOf(invoice: Pick<Invoice, "summary">): string | null {
  const m = /billed to (.+?), not /i.exec(invoice.summary ?? "");
  return m ? m[1] : null;
}

// ---------------------------------------------------------------- the pieces

export function LadderButton({ a, size = "sm", viewer }: { a: LadderAction; size?: "sm" | "md"; viewer?: boolean }) {
  if (a.link) return <a onClick={viewer ? undefined : a.onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, font: "700 14px var(--font-body)", color: viewer ? "var(--text-subtle)" : "var(--text-link)", cursor: viewer ? "default" : "pointer", padding: "0 4px" }}>{a.label}<Icon name="external-link" size={13} /></a>;
  const el = <Button size={size} variant={a.primary ? "primary" : "outline"} disabled={a.disabled || viewer} leadingIcon={<Icon name={a.icon} size={size === "sm" ? 14 : 16} />} onClick={a.onClick} style={{ whiteSpace: "nowrap" }}>{a.label}</Button>;
  return a.tip ? <Hint text={a.tip} width={260} below><span style={{ display: "inline-flex" }}>{el}</span></Hint> : el;
}

/** The ⋯ menu: the actions after the primary, then Upload a corrected invoice… and Reject invoice…. */
export function LadderMenu({ ladder, viewer, open, onToggle }: { ladder: Ladder; viewer?: boolean; open: boolean; onToggle: () => void }) {
  const items = [...ladder.rest, null, ladder.replace, ladder.close];
  return (
    <span style={{ position: "relative", display: "inline-flex" }} onClick={(e) => e.stopPropagation()}>
      <IconButton size="sm" icon={<Icon name="ellipsis" size={18} />} label="More actions" disabled={viewer} onClick={onToggle} />
      {open && (
        <span role="menu" style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 40, width: 270, padding: 6, borderRadius: "var(--radius-md)", background: "var(--surface-card)", boxShadow: "var(--shadow-lg)", display: "flex", flexDirection: "column", textAlign: "left" }}>
          {items.map((it, i) => it === null ? <i key={i} style={{ display: "block", height: 1, margin: "5px 6px", background: "var(--border-subtle)" }} /> : (
            <button key={it.label} type="button" role="menuitem" className="avp-mi" disabled={it.disabled} title={it.tip} onClick={() => { onToggle(); it.onClick(); }} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, border: 0, background: "transparent", cursor: it.disabled ? "not-allowed" : "pointer", font: "600 14px var(--font-body)", color: it.disabled ? "var(--text-subtle)" : "var(--text-strong)", textAlign: "left" }}>
              <Icon name={it.icon} size={15} style={{ color: "var(--text-muted)" }} />{it.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

export function Working({ text }: { text: string }) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 8, font: "600 13px var(--font-body)", color: "var(--text-muted)" }}><Spinner size={14} />{text}</span>;
}

export { mono, plainSummary };
